import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Form, FormGroup } from 'reactstrap';
import { Field, change, reduxForm, formValueSelector } from 'redux-form';
import {
  isRequired,
  validateIntegerNumber,
} from '../../../../utils/validators';
import FormItem from '../../../common/forms/FormItem';
import Loader from '../../../common/Loader';
import FormFooter from '../../../common/forms/FormFooter';
import TextInput from '../../../common/forms/input/TextInput';
import RegisteredBusSelect from '../../../common/forms/select/RegisteredBusSelect';
import Select from '../../../common/forms/select/Select';
import { enumToSelectOptions } from '../../../../utils/enum';
import {
  MAINTENANCE_WARNING_TYPE,
  MAINTENANCE_WARNING_PRIORITY,
  MAINTENANCE_WARNING_RECURRENCE,
  MAINTENANCE_WARNING_STATUS,
} from '../../../../config/constants';
import MaintenanceWarningReporterSelect from '../../../common/forms/select/MaintenanceWarningReporterSelect';
import MaintenanceFailModeSelect from '../../../common/forms/select/MaintenanceFailModeSelect';
import { DATE_TIME_FORMAT } from '../../../../config/locale';

const MAINTENANCE_WARNING_FORM_NAME = 'MaintenanceWarningForm';

const buildActivityLabel = (maintenanceFailModeActivity) => {
  const { activity } = maintenanceFailModeActivity;

  if (!activity) return 'Actividad';

  if (activity.companyActivityId) {
    return `${activity.companyActivityId} - ${activity.name}`;
  }

  return activity.name;
};

export const MaintenanceWarningForm = ({
  handleSubmit,
  loading,
  dispatchChange,
  editionMode,
  requiredField,
  registeredBusId,
}) => {
  const [failModeCategoryOptions, setFailModeCategoryOptions] = React.useState(
    [],
  );

  const [failModeActivityOptions, setFailModeActivityOptions] = React.useState(
    [],
  );

  const disabledInEditionMode = editionMode;

  const clearFailModeDependentFields = () => {
    dispatchChange(MAINTENANCE_WARNING_FORM_NAME, 'priority', null);

    dispatchChange(
      MAINTENANCE_WARNING_FORM_NAME,
      'maintenanceFailModeCategoryId',
      null,
    );

    dispatchChange(
      MAINTENANCE_WARNING_FORM_NAME,
      'maintenanceFailModeActivityList',
      [],
    );

    setFailModeCategoryOptions([]);
    setFailModeActivityOptions([]);
  };

  const onChangeRegisteredBus = (option) => {
    if (option) {
      dispatchChange(
        MAINTENANCE_WARNING_FORM_NAME,
        'actualVirtualOdometer',
        option.actualVirtualOdometer,
      );
    } else {
      dispatchChange(
        MAINTENANCE_WARNING_FORM_NAME,
        'actualVirtualOdometer',
        null,
      );

      dispatchChange(
        MAINTENANCE_WARNING_FORM_NAME,
        'actualRegisteredBusOdometer',
        null,
      );

      dispatchChange(
        MAINTENANCE_WARNING_FORM_NAME,
        'maintenanceFailModeId',
        null,
      );

      clearFailModeDependentFields();
    }
  };

  const onChangeFailMode = (option) => {
    if (!option) {
      clearFailModeDependentFields();
      return;
    }

    dispatchChange(
      MAINTENANCE_WARNING_FORM_NAME,
      'priority',
      option.priority ? MAINTENANCE_WARNING_PRIORITY[option.priority] : null,
    );

    dispatchChange(
      MAINTENANCE_WARNING_FORM_NAME,
      'maintenanceFailModeCategoryId',
      null,
    );

    dispatchChange(
      MAINTENANCE_WARNING_FORM_NAME,
      'maintenanceFailModeActivityList',
      [],
    );

    const categoryList = option.maintenanceFailModeCategoryList || [];

    if (!categoryList.length) {
      setFailModeCategoryOptions([]);
    } else {
      const categoryOptions = categoryList.map((category) => ({
        value: category.id,
        label: category.name,
        raw: category,
      }));

      setFailModeCategoryOptions(categoryOptions);

      if (categoryOptions.length === 1) {
        dispatchChange(
          MAINTENANCE_WARNING_FORM_NAME,
          'maintenanceFailModeCategoryId',
          categoryOptions[0],
        );
      }
    }

    const activityList = option.maintenanceFailModeActivityList || [];

    if (!activityList.length) {
      setFailModeActivityOptions([]);
      return;
    }

    const activityOptions = activityList.map((maintenanceFailModeActivity) => ({
      value:
        maintenanceFailModeActivity.activityId ||
        (maintenanceFailModeActivity.activity &&
          maintenanceFailModeActivity.activity.id),
      label: buildActivityLabel(maintenanceFailModeActivity),
      maintenanceFailModeActivityId: maintenanceFailModeActivity.id,
      raw: maintenanceFailModeActivity,
    }));

    setFailModeActivityOptions(activityOptions);
  };

  if (loading) return <Loader />;

  return (
    <Form onSubmit={handleSubmit}>
      <FormGroup row>
        <FormItem label="Reportado por" required>
          <Field
            name="reporterCustomerId"
            component={MaintenanceWarningReporterSelect}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Fecha y hora" required>
          <Field
            name="issueDate"
            component={TextInput}
            type="datetime-local"
            placeholder={DATE_TIME_FORMAT}
            validate={[isRequired]}
            disabled={disabledInEditionMode}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Bus" required={requiredField}>
          <Field
            name="registeredBusId"
            component={RegisteredBusSelect}
            onChange={onChangeRegisteredBus}
            validate={requiredField ? [isRequired] : null}
            isClearable
            isDisabled={disabledInEditionMode}
          />
        </FormItem>
      </FormGroup>

      {registeredBusId ? (
        <>
          <FormGroup row>
            <FormItem label="KM Actual">
              <Field
                name="actualVirtualOdometer"
                component={TextInput}
                placeholder="KM Actual"
                disabled
              />
            </FormItem>
          </FormGroup>

          <FormGroup row>
            <FormItem label="KM Ocurrencia" required={requiredField}>
              <Field
                name="actualRegisteredBusOdometer"
                component={TextInput}
                placeholder="KM Ocurrencia"
                validate={
                  requiredField
                    ? [isRequired, validateIntegerNumber]
                    : [validateIntegerNumber]
                }
                disabled={disabledInEditionMode}
              />
            </FormItem>
          </FormGroup>
        </>
      ) : null}

      <FormGroup row>
        <FormItem label="Tipo de Ocurrencia" required={requiredField}>
          <Field
            name="maintenanceWarningType"
            component={Select}
            options={enumToSelectOptions(MAINTENANCE_WARNING_TYPE)}
            validate={requiredField ? [isRequired] : null}
            isDisabled={disabledInEditionMode}
          />
        </FormItem>
      </FormGroup>

      {registeredBusId ? (
        <>
          <FormGroup row>
            <FormItem label="Modo Falla" required={requiredField}>
              <Field
                name="maintenanceFailModeId"
                component={MaintenanceFailModeSelect}
                onChange={onChangeFailMode}
                validate={requiredField ? [isRequired] : null}
                isClearable
                isDisabled={disabledInEditionMode}
              />
            </FormItem>
          </FormGroup>

          {failModeCategoryOptions.length ? (
            <FormGroup row>
              <FormItem label="Categoría" required={requiredField}>
                <Field
                  name="maintenanceFailModeCategoryId"
                  component={Select}
                  options={failModeCategoryOptions}
                  validate={requiredField ? [isRequired] : null}
                  isClearable={failModeCategoryOptions.length > 1}
                  isDisabled={
                    disabledInEditionMode ||
                    failModeCategoryOptions.length === 1
                  }
                />
              </FormItem>
            </FormGroup>
          ) : null}

          {failModeActivityOptions.length ? (
            <FormGroup row>
              <FormItem label="Actividades" required={requiredField}>
                <Field
                  name="activityId"
                  component={Select}
                  options={failModeActivityOptions}
                  validate={requiredField ? [isRequired] : null}
                  isDisabled={disabledInEditionMode}
                />
              </FormItem>
            </FormGroup>
          ) : null}
        </>
      ) : null}

      <FormGroup row>
        <FormItem label="Prioridad" required={requiredField}>
          <Field
            name="priority"
            component={Select}
            options={enumToSelectOptions(MAINTENANCE_WARNING_PRIORITY)}
            validate={requiredField ? [isRequired] : null}
            isDisabled={disabledInEditionMode}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Reincidencia">
          <Field
            name="recurrence"
            component={Select}
            options={MAINTENANCE_WARNING_RECURRENCE}
            isDisabled={disabledInEditionMode}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Comentario">
          <Field
            name="comments"
            component={TextInput}
            type="textarea"
            maxLength={200}
          />
        </FormItem>
      </FormGroup>

      <FormGroup row>
        <FormItem label="Estado" required={requiredField}>
          <Field
            name="status"
            component={Select}
            options={enumToSelectOptions(MAINTENANCE_WARNING_STATUS)}
            validate={requiredField ? [isRequired] : null}
            isDisabled={!editionMode}
          />
        </FormItem>
      </FormGroup>

      <FormFooter />
    </Form>
  );
};

MaintenanceWarningForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  dispatchChange: PropTypes.func.isRequired,
  editionMode: PropTypes.bool,
  requiredField: PropTypes.bool,
  registeredBusId: PropTypes.shape({
    value: PropTypes.number,
    label: PropTypes.string,
  }),
};

MaintenanceWarningForm.defaultProps = {
  editionMode: false,
  requiredField: true,
  registeredBusId: null,
};

const selector = formValueSelector(MAINTENANCE_WARNING_FORM_NAME);

const mapStateToProps = ({ MechanicalMaintenanceUnit, form }) => ({
  loading: !MechanicalMaintenanceUnit.MaintenanceWarning.getIn([
    'current',
    'activity',
  ]).isEmpty(),
  registeredBusId: selector({ form }, 'registeredBusId'),
});

const mapDispatchToProps = {
  dispatchChange: change,
};

const formComponent = reduxForm({
  form: MAINTENANCE_WARNING_FORM_NAME,
})(MaintenanceWarningForm);

export default connect(mapStateToProps, mapDispatchToProps)(formComponent);
